import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { XhrHttpHandler } from '@aws-sdk/xhr-http-handler';
import store from '../../app/store';
import { apiSlice } from '../../app/services/apiSlice';
import {
  resetImagesGalleryState,
  setGalleryImagesProgress,
  setGalleryImagesUploading,
  setVideoUploadProgress,
} from '../../app/services/imagesGallery';
import {
  checkImageURL,
  checkVideoURL,
  generateUploadFileName,
} from '../../utils/helper';
import { s3UploadBaseQuery } from './CustomUploadImageBaseQuery';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

const AUTH_URL = 'vendor/api/floristImages';

const VENDOR_URL = 'vendor/api';

//test

export const forumApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllFloristImagesGallery: builder.query({
      query: ({ params }) => ({
        url: `${AUTH_URL}`,
        params,
      }),
    }),
    uploadImage: builder.mutation({
      query: (data) => ({
        url: `${AUTH_URL}`,
        method: 'POST',
        body: { ...data },
      }),
    }),
    editImage: builder.mutation({
      query: (data) => ({
        url: `${AUTH_URL}`,
        method: 'PUT',
        body: { ...data },
      }),
    }),
    deleteImage: builder.mutation({
      query: (id) => ({
        url: `${AUTH_URL}/deleteByImageId/${id}`,
        method: 'DELETE',
      }),
    }),
    setFavoriteImage: builder.mutation({
      query: (data) => ({
        url: `${VENDOR_URL}/favouriteImage`,
        method: 'POST',
        body: { ...data },
      }),
    }),
    uploadFloristImages: builder.mutation({
      query: ({ formData }) => ({
        url: `${AUTH_URL}/v2/multiple/files`,
        method: 'POST',
        body: formData,
      }),
    }),
    uploadImageToS3Bucket: builder.mutation({
      queryFn: async (data, _api, _extraOptions, baseQuery) => {
        try {
          const s3 = new S3Client({
            region: process.env.REACT_APP_S3_BUCKET_REGION,
            requestHandler: new XhrHttpHandler({}),
          });

          const updatedImagesDTO = await Promise.all(
            data.floristImagesDTOS?.map(async (image, index) => {
              const extension = image?.originalFile?.name?.split('.').pop();
              const newFileName = `${uuidv4()}.${extension}`;

              const imagePath = `image-gallery/${
                data.uuid ? `${data.uuid}/` : ''
              }${newFileName}`;

              const preSignedUrlPayload = {
                bucketName: process.env.REACT_APP_S3_BUCKET_UPLOAD_URL,
                path: imagePath,
                contentType: image?.originalFile?.type,
              };

              const response = await baseQuery({
                url: `vendor/api/floristImages/generatePresignedUrl`,
                method: 'POST',
                body: { ...preSignedUrlPayload },
              });

              const presignedUrl = response?.error?.data
                .replace(/^Presigned URL:\s*/, '')
                .trim();
              
              const fileBuffer = await image.originalFile.arrayBuffer();
              await axios.put(presignedUrl, fileBuffer, {
                headers: {
                  'Content-Type': image?.originalFile?.type,
                },
                onUploadProgress: (progressEvent) => {
                  const { loaded, total } = progressEvent;
                  const currentProgress = parseInt((loaded * 100) / (total ?? 1));
              
                  store.dispatch(
                    setGalleryImagesProgress({
                      index,
                      progress: currentProgress,
                      isFlorist: data.isFlorist,
                    })
                  );
                },
              });

              const s3BucketUrl = `https://${process.env.REACT_APP_S3_BUCKET_URL}/${imagePath}`;
              await checkImageURL(s3BucketUrl);

              const updatedImageDTO = {
                ...image,
                imageDTO: {
                  ...image.imageDTO,
                  url: s3BucketUrl,
                },
              };

              delete updatedImageDTO.originalFile;
              return updatedImageDTO;
            }),
          );

          const updatedPayload = {
            floristImagesDTOS: updatedImagesDTO,
            floristId: data.floristId ?? null,
          };

          const response = await baseQuery({
            url: `${AUTH_URL}`,
            method: 'POST',
            body: { ...updatedPayload },
          });

          store.dispatch(
            setGalleryImagesUploading({
              uploading: false,
              isFlorist: data.isFlorist,
            }),
          );
          store.dispatch(resetImagesGalleryState());

          return { data: response.data };
        } catch (error) {
          console.log('error: ', error);
          return { error };
        }
      },
    }),
    uploadVideoToS3Bucket: builder.mutation({
      queryFn: async (data, _api, _extraOptions) => {
        try {
          const s3 = new S3Client({
            region: process.env.REACT_APP_S3_BUCKET_REGION,
            requestHandler: new XhrHttpHandler({}),
          });
          const video = data?.floristVideoDTOS;
          if (!video || !video.originalFile) {
            throw new Error('No video file provided');
          }

          const newFileName = generateUploadFileName(video.originalFile);
          const videoPath = `video-gallery/${
            data.uuid ? `${data.uuid}/` : ''
          }${newFileName}`;

          const params = {
            Bucket: process.env.REACT_APP_S3_BUCKET_UPLOAD_URL,
            Key: videoPath,
            Body: video.originalFile,
          };
          const upload = new Upload({
            client: s3,
            params,
          });

          upload.on('httpUploadProgress', (progress) => {
            const currentProgress = parseInt(
              (progress.loaded * 100) / progress.total,
            );

            store.dispatch(
              setVideoUploadProgress({
                progress: currentProgress,
              }),
            );
          });
          await upload.done();
          const s3BucketUrl = `https://${
            process.env.REACT_APP_ENV === 'dev'
              ? 'thumbnails.dev.boostfloral.com'
              : 'thumbnails.boostfloral.com'
          }/${videoPath}`;

          await checkVideoURL(s3BucketUrl);
          const updatedVideoDTO = {
            ...video,
            videoDTO: {
              ...video.videoDTO,
              url: s3BucketUrl,
            },
          };
          delete updatedVideoDTO.originalFile;

          return { data: s3BucketUrl };
        } catch (error) {
          console.log('error: ', error);
          return { error };
        }
      },
    }),
  }),
});
export const {
  useGetAllFloristImagesGalleryQuery,
  useLazyGetAllFloristImagesGalleryQuery,
  useUploadImageMutation,
  useDeleteImageMutation,
  useEditImageMutation,
  useSetFavoriteImageMutation,
  useUploadFloristImagesMutation,
  useUploadImageToS3BucketMutation,
  useUploadVideoToS3BucketMutation,
} = forumApiSlice;
